import React, { useMemo, useState } from "react";
import { formatDate } from "../../lib/format";
import { CustomIcon } from "../shared/CustomIcon";
import { apiDeleteBlockout } from "../../lib/calendarApi";

// ============================================================
// BlockoutManagerPanel — inline management table on the Calendar
// page. Lets admins filter (Mine / All / Month / 90 Days), edit
// a row in-place (pre-fills the modal), or hard-delete it. Read
// the latest blockouts list and re-renders when it changes.
// ============================================================
const FILTER_OPTIONS = [
  { id: "mine",      label: "Mine" },
  { id: "all",       label: "All" },
  { id: "month",     label: "This Month" },
  { id: "90days",    label: "Next 90 Days" },
];

function inWindow(b, today, days) {
  if (!b?.startDate) return false;
  const end = new Date(today); end.setDate(end.getDate() + days); end.setHours(23, 59, 59, 999);
  const start = new Date(b.startDate);
  const stop  = new Date(b.endDate || b.startDate);
  return stop >= today && start <= end;
}

function inCalendarMonth(b, today) {
  if (!b?.startDate) return false;
  const start = new Date(b.startDate);
  return start.getFullYear() === today.getFullYear() && start.getMonth() === today.getMonth();
}

function audienceLabel(b, usersById) {
  // Missing appliesTo behaves like "all" so legacy/API-injected blockouts
  // degrade gracefully instead of rendering as an unhelpful "—".
  if (b?.appliesTo === "all" || b?.appliesTo == null) return "Everyone";
  if (Array.isArray(b.appliesTo)) {
    if (b.appliesTo.length === 0) return "No one";
    if (b.appliesTo.length === 1) {
      const u = usersById[b.appliesTo[0]];
      return u?.name || u?.username || "1 user";
    }
    return `${b.appliesTo.length} users`;
  }
  return "—";
}

export default function BlockoutManagerPanel({
  currentUser, isSuperAdmin, isLimitedAdmin, blockouts, setBlockouts, users, onEdit,
}) {
  const isAdmin = !!(isSuperAdmin || isLimitedAdmin);
  const [filter, setFilter] = useState(isAdmin ? "all" : "mine");
  const [busyId, setBusyId] = useState(null);

  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const usersById = useMemo(() => Object.fromEntries((users || []).map(u => [u.id, u])), [users]);

  const filtered = useMemo(() => {
    const list = (blockouts || []).filter(Boolean);
    let f = list;
    if (filter === "mine") {
      f = list.filter(b => {
        const me = currentUser?.id;
        if (!me) return false;
        if (b.createdBy === me) return true;
        if (b.appliesTo === "all") return true;
        if (Array.isArray(b.appliesTo) && b.appliesTo.includes(me)) return true;
        return false;
      });
    } else if (filter === "month") {
      f = list.filter(b => inCalendarMonth(b, today));
    } else if (filter === "90days") {
      f = list.filter(b => inWindow(b, today, 90));
    }
    return f.slice().sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));
  }, [blockouts, filter, currentUser, today]);

  const removeRow = async (b) => {
    if (!window.confirm(`Delete blockout "${b.name}"?`)) return;
    setBusyId(b.id);
    try {
      await apiDeleteBlockout(b.id);
      setBlockouts(prev => prev.filter(x => x.id !== b.id));
    } catch (e) {
      alert(e.message || "Could not delete blockout");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="blockout-manager-panel" className="card" style={{ padding: 14, marginTop: 16, background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, color: "var(--kc-accent)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 8 }}><CustomIcon name="warning" size={14} /> Blockouts</div>
        <div data-testid="bo-filter-bar" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {FILTER_OPTIONS.map(opt => {
            const active = filter === opt.id;
            return (
              <button
                key={opt.id}
                data-testid={`bo-filter-${opt.id}`}
                onClick={() => setFilter(opt.id)}
                style={{
                  padding: "4px 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                  textTransform: "uppercase", borderRadius: 999, cursor: "pointer",
                  background: active ? "var(--kc-accent)" : "transparent",
                  color: active ? "#1A1A1A" : "var(--kc-accent)",
                  border: `1px solid ${active ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 40%, transparent)"}`,
                  fontFamily: "'Montserrat',sans-serif",
                }}
              >{opt.label}</button>
            );
          })}
        </div>
      </div>
      {filtered.length === 0 ? (
        <div data-testid="bo-empty" style={{ padding: 20, fontSize: 12, color: "var(--kc-meta)", textAlign: "center", fontFamily: "'Montserrat',sans-serif" }}>
          No blockouts match this filter.
        </div>
      ) : (
        <div data-testid="bo-list" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {filtered.map(b => (
            <div key={b.id} data-testid={`bo-row-${b.id}`} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: "color-mix(in srgb, var(--kc-accent) 5%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)", borderRadius: 8, fontFamily: "'Montserrat',sans-serif" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "var(--kc-text)", marginBottom: 2 }}>{b.name || "Untitled blockout"}</div>
                <div style={{ fontSize: 11, color: "var(--kc-sub)" }}>
                  {formatDate(b.startDate)}{b.endDate && b.endDate !== b.startDate ? ` → ${formatDate(b.endDate)}` : ""}
                  {b.allDay ? " · All day" : ""}
                  {" · "}
                  <span style={{ color: "color-mix(in srgb, var(--kc-accent) 70%, transparent)" }}>{audienceLabel(b, usersById)}</span>
                </div>
              </div>
              {isAdmin && (
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button
                    className="btn btn-sm btn-outline"
                    data-testid={`bo-edit-${b.id}`}
                    onClick={() => onEdit && onEdit(b)}
                  >Edit</button>
                  <button
                    className="btn btn-sm btn-danger"
                    data-testid={`bo-delete-${b.id}`}
                    onClick={() => removeRow(b)}
                    disabled={busyId === b.id}
                  >{busyId === b.id ? "…" : "Delete"}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
